import { itemDef } from "@eldrun/content";
import type { MatchStateType } from "@eldrun/protocol";
import { Box3, BoxGeometry, BufferAttribute, Color, DynamicDrawUsage, Group, InstancedMesh, Matrix4, Quaternion, Vector3, type BufferGeometry, type Object3D, type ShaderMaterial } from "three";
import { itemMesh } from "../render/item-models.ts";
import type { DissolvePart } from "./dissolves.ts";
import { models, type ModelLibrary } from "../render/model-library.ts";
import { QUALITY } from "../render/quality.ts";

const CATEGORY_COLORS: Record<string, string> = {
  material: "#B08A5A",
  comida: "#E8A23A",
  ferramenta: "#9AA4B0",
  arma: "#C8402E",
  utilitario: "#7BD389",
};

/** Cor do cubo de um item: a da categoria dele. */
const categoryColor = (itemId: string, out: Color): Color => out.set(CATEGORY_COLORS[itemDef(itemId)?.kind ?? "material"] ?? "#B08A5A");

/**
 * Só o drop que sai até esta distância da câmera (m) se desfaz (F95); mais longe, só some. Uma leva de
 * drops expirando longe não ocupa o pool.
 */
export const DROP_DISSOLVE_RADIUS = 30;

/** Só os drops até esta distância são desenhados (vezes a distância de visão). */
export const DROP_RADIUS = 90;
/**
 * Tipos de item com malha própria (F47): os dos drops mais próximos, uma `InstancedMesh` cada. Os drops
 * dos outros tipos viram o cubo colorido pela categoria, numa malha só: no máximo 17 draw calls.
 */
export const DROP_KINDS = 16;
/** Maior medida (m) de um item no chão: as compridas (lança, espada, arco) encolhem até ela. */
export const DROP_SIZE = 0.55;
/** Malhas de tipo guardadas sem uso (para o tipo que volta não refazer a malha); as mais antigas saem. */
const KEEP_KINDS = DROP_KINDS * 2;
const INITIAL_CAPACITY = 16;
/** Altura (m) do centro do item acima do chão, no mínimo (a do cubo de antes), e a folga sob ele. */
const LIFT = 0.35;
const CLEARANCE = 0.12;
/** Flutuação (m) e velocidades do giro e do sobe-e-desce, as de antes da F47. */
const BOB = 0.08;

const m4 = new Matrix4();
const pos = new Vector3();
const offset = new Vector3();
const q = new Quaternion();
const axis = new Vector3(0, 1, 0);
const scale = new Vector3();
const color = new Color();
const box = new Box3();

/** Malha de um tipo de item no chão: a geometria, a escala e o centro dela (o ponto que flutua e gira). */
interface Kind {
  mesh: InstancedMesh;
  scale: number;
  center: Vector3;
  /** Altura do centro acima do chão. */
  lift: number;
  /** Quadro em que o tipo foi desenhado pela última vez. */
  frame: number;
}

export interface DropStats {
  /** Drops desenhados agora. */
  drawn: number;
  /** Tipos com malha própria desenhados agora (até `DROP_KINDS`). */
  kinds: number;
  /** Drops que viraram cubo (tipos além dos `DROP_KINDS` mais próximos). */
  cubes: number;
  /** Malhas visíveis: os draw calls dos drops. */
  meshes: number;
}

/** O que a camada lê de um drop (o `ItemDrop` do estado). */
export interface DropLike {
  itemId: string;
  x: number;
  y: number;
  z: number;
}

export interface DropLayerOptions {
  library?: ModelLibrary;
}

/**
 * Itens no chão (só visual; a posição vem do servidor), flutuando e girando: o modelo do item (F47,
 * LOD 1, com a cor do tier) ou a malha procedural, uma `InstancedMesh` por tipo para os `DROP_KINDS`
 * tipos mais próximos; o resto, cubinhos coloridos pela categoria. Malha vazia fica invisível.
 */
export class DropLayer {
  private readonly group = new Group();
  private readonly material: ShaderMaterial;
  private readonly library: ModelLibrary;
  private readonly cubeGeometry: BoxGeometry;
  private cubes: InstancedMesh;
  private readonly kinds = new Map<string, Kind>();
  private readonly near: { key: string; d2: number }[] = [];
  private readonly chosen = new Map<string, number>();
  private frame = 0;
  /** O instante e a câmera do último `update` (a pose e o raio do drop que sai, F95). */
  private time = 0;
  private cameraX = 0;
  private cameraZ = 0;
  private current: DropStats = { drawn: 0, kinds: 0, cubes: 0, meshes: 0 };

  constructor(root: Object3D, material: ShaderMaterial, options: DropLayerOptions = {}) {
    this.material = material;
    this.library = options.library ?? models.library;
    this.group.name = "drops";
    root.add(this.group);
    this.cubeGeometry = new BoxGeometry(0.28, 0.28, 0.28).toNonIndexed() as BoxGeometry;
    const count = this.cubeGeometry.getAttribute("position").count;
    // Cor branca por vértice (a cor vem da instância) e sem vento.
    this.cubeGeometry.setAttribute("color", new BufferAttribute(new Float32Array(count * 3).fill(1), 3));
    this.cubeGeometry.setAttribute("wind", new BufferAttribute(new Float32Array(count), 1));
    this.cubeGeometry.setAttribute("aCrown", new BufferAttribute(new Float32Array(count * 3), 3));
    this.cubes = this.instanced(this.cubeGeometry, 256);
  }

  private instanced(geometry: BufferGeometry, capacity: number): InstancedMesh {
    const mesh = new InstancedMesh(geometry, this.material, capacity);
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.visible = false;
    this.group.add(mesh);
    return mesh;
  }

  /** Troca a malha por uma com capacidade para `needed` (dobrando), se faltar. */
  private ensure(mesh: InstancedMesh, needed: number): InstancedMesh {
    if (needed <= mesh.instanceMatrix.count) return mesh;
    const grown = this.instanced(mesh.geometry, Math.max(needed, mesh.instanceMatrix.count * 2));
    grown.name = mesh.name;
    mesh.removeFromParent();
    mesh.dispose();
    return grown;
  }

  private kind(itemId: string): Kind {
    let kind = this.kinds.get(itemId);
    if (kind) return kind;
    const geometry = itemMesh(itemId, 1, this.library);
    box.setFromBufferAttribute(geometry.getAttribute("position") as BufferAttribute);
    const size = box.getSize(new Vector3());
    const s = Math.min(1, DROP_SIZE / Math.max(size.x, size.y, size.z, 1e-3));
    const mesh = this.instanced(geometry, INITIAL_CAPACITY);
    mesh.name = itemId;
    kind = { mesh, scale: s, center: box.getCenter(new Vector3()), lift: Math.max(LIFT, (size.y * s) / 2 + BOB + CLEARANCE), frame: 0 };
    this.kinds.set(itemId, kind);
    return kind;
  }

  /** Desenha os drops perto da câmera: os tipos dos mais próximos com a própria malha, o resto em cubos. */
  update(state: MatchStateType, timeSeconds: number, cameraX: number, cameraZ: number): void {
    this.frame++;
    this.time = timeSeconds;
    this.cameraX = cameraX;
    this.cameraZ = cameraZ;
    this.near.length = 0;
    const radius = DROP_RADIUS * QUALITY.distance;
    state.drops.forEach((drop, key) => {
      const d2 = (drop.x - cameraX) ** 2 + (drop.z - cameraZ) ** 2;
      if (d2 <= radius * radius) this.near.push({ key, d2 });
    });
    this.near.sort((a, b) => a.d2 - b.d2);
    // Tipos na ordem do drop mais perto de cada um, e quantos drops de cada tipo e de cubo.
    this.chosen.clear();
    let cubeCount = 0;
    for (const { key } of this.near) {
      const id = state.drops.get(key)?.itemId ?? "";
      const n = this.chosen.get(id);
      if (n !== undefined) this.chosen.set(id, n + 1);
      // Item que este cliente não conhece (conteúdo mais novo no servidor) fica no cubo, como antes.
      else if (this.chosen.size < DROP_KINDS && itemDef(id)) this.chosen.set(id, 1);
      else cubeCount++;
    }
    for (const [id, n] of this.chosen) {
      const kind = this.kind(id);
      kind.mesh = this.ensure(kind.mesh, n);
      kind.mesh.count = 0;
      kind.frame = this.frame;
    }
    this.cubes = this.ensure(this.cubes, cubeCount);
    this.cubes.count = 0;

    for (const { key } of this.near) {
      const drop = state.drops.get(key);
      if (!drop) continue;
      const kind = this.chosen.has(drop.itemId) ? this.kinds.get(drop.itemId)! : undefined;
      const mesh = kind?.mesh ?? this.cubes;
      mesh.setMatrixAt(mesh.count, this.pose(key, drop, timeSeconds, kind));
      if (!kind) mesh.setColorAt(mesh.count, categoryColor(drop.itemId, color));
      mesh.count++;
    }

    let drawn = 0;
    let meshes = 0;
    const stale: string[] = [];
    for (const [id, kind] of this.kinds) {
      const live = kind.frame === this.frame && kind.mesh.count > 0;
      if (!live) kind.mesh.count = 0;
      kind.mesh.visible = live;
      if (live) kind.mesh.instanceMatrix.needsUpdate = true;
      drawn += kind.mesh.count;
      meshes += Number(live);
      if (!live && this.frame - kind.frame > 60) stale.push(id);
    }
    // Sem uso há mais de 60 quadros e além do que se guarda: as mais antigas saem.
    stale.sort((a, b) => this.kinds.get(a)!.frame - this.kinds.get(b)!.frame);
    for (const id of stale.slice(0, Math.max(0, this.kinds.size - KEEP_KINDS))) this.forget(id);
    this.cubes.visible = this.cubes.count > 0;
    this.cubes.instanceMatrix.needsUpdate = true;
    if (this.cubes.instanceColor) this.cubes.instanceColor.needsUpdate = true;
    this.current = { drawn: drawn + this.cubes.count, kinds: meshes, cubes: this.cubes.count, meshes: meshes + Number(this.cubes.visible) };
  }

  /** A matriz do drop no instante `time` (s): gira e flutua em volta do centro da caixa do item (o punho dos modelos fica na ponta do cabo). */
  private pose(key: string, drop: DropLike, time: number, kind: Kind | undefined): Matrix4 {
    const phase = (Number(key) * 1.37) % 6.28;
    const bob = Math.sin(time * 2 + phase) * BOB;
    q.setFromAxisAngle(axis, time * 1.2 + phase);
    const s = kind?.scale ?? 1;
    if (kind) offset.copy(kind.center).multiplyScalar(s).applyQuaternion(q);
    else offset.set(0, 0, 0);
    pos.set(drop.x, drop.y + (kind?.lift ?? LIFT) + bob, drop.z).sub(offset);
    return m4.compose(pos, q, scale.setScalar(s));
  }

  /**
   * O drop que saiu do estado (apanhado ou expirado: o estado não diz qual) para o dissolve (F95): a malha
   * com que ele foi desenhado no último quadro (a do tipo ou o cubo, na cor da categoria) e a pose daquele
   * instante, só em pontilhado (sai a toda hora: sem a borda acesa). Além de `DROP_DISSOLVE_RADIUS` (ou do
   * raio de desenho, se menor), nada. O `update` seguinte já não o desenha.
   */
  dissolvePart(key: string, drop: DropLike): DissolvePart | undefined {
    const radius = Math.min(DROP_DISSOLVE_RADIUS, DROP_RADIUS * QUALITY.distance);
    if ((drop.x - this.cameraX) ** 2 + (drop.z - this.cameraZ) ** 2 > radius * radius) return undefined;
    const kind = this.chosen.has(drop.itemId) ? this.kinds.get(drop.itemId) : undefined;
    const matrix = new Matrix4().copy(this.pose(key, drop, this.time, kind));
    if (kind) return { geometry: kind.mesh.geometry, matrix, material: this.material, dither: true };
    return { geometry: this.cubeGeometry, matrix, material: this.material, color: categoryColor(drop.itemId, new Color()), dither: true };
  }

  private forget(itemId: string): void {
    const kind = this.kinds.get(itemId);
    if (!kind) return;
    kind.mesh.removeFromParent();
    // A geometria é do item (cache procedural ou vista do modelo): só a instância sai.
    kind.mesh.dispose();
    this.kinds.delete(itemId);
  }

  get drawn(): number {
    return this.current.drawn;
  }

  stats(): DropStats {
    return { ...this.current };
  }

  dispose(): void {
    for (const id of [...this.kinds.keys()]) this.forget(id);
    this.cubes.removeFromParent();
    this.cubes.dispose();
    this.cubeGeometry.dispose();
    this.group.removeFromParent();
  }
}
